import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Wrench, Activity, Globe, ShieldAlert, CheckCircle2, XCircle, AlertTriangle,
  Cpu, HardDrive, MemoryStick, RefreshCw, Ban, Trash2, Zap, Database, Gauge,
  Layers, ScrollText, Play, RotateCw, ExternalLink, Send, Info, Mail,
  LogIn, Server, MousePointerClick,
} from "lucide-react";
import { http } from "@/lib/api";
import { PageHeader } from "@/components/kit/PageHeader";
import { DataTable } from "@/components/kit/DataTable";
import { TextField, SwitchField, SelectField } from "@/components/kit/fields";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/kit/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { fmtDate, fmtNumber } from "@/lib/format";
import { cn } from "@/lib/utils";
import EmailSettingsTab from "@/components/system/EmailSettings";
import BackupsTab from "@/components/system/BackupsTab";

const SUBTABS = [
  { id: "maintenance", label: "Maintenance", icon: Wrench },
  { id: "health", label: "Health", icon: Activity },
  { id: "security", label: "Security", icon: ShieldAlert },
  { id: "email", label: "Email", icon: Mail },
  { id: "backups", label: "Backups", icon: Database },
];
const SEV = { high: "#EF4444", medium: "#EAB308", low: "#3B82F6" };

function Panel({ title, children, testId, right }) {
  return (
    <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-5" data-testid={testId}>
      <div className="mb-4 flex items-center justify-between"><h3 className="text-sm font-medium text-foreground">{title}</h3>{right}</div>
      {children}
    </div>
  );
}
function Meter({ label, percent, icon: Icon, sub }) {
  const p = percent ?? 0;
  const color = p > 85 ? "#EF4444" : p > 65 ? "#EAB308" : "#22C55E";
  return (
    <div className="rounded-lg border border-white/[0.06] bg-[#0A0A0A] p-4">
      <div className="mb-2 flex items-center gap-2 text-sm text-muted-foreground"><Icon className="h-4 w-4" />{label}</div>
      <div className="text-xl font-semibold text-foreground">{percent != null ? `${percent}%` : "—"}</div>
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/[0.06]"><div className="h-full rounded-full transition-all" style={{ width: `${p}%`, backgroundColor: color }} /></div>
      {sub && <div className="mt-1.5 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

// ------------------------------------------------------------- Maintenance
function MaintenanceTab() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["system-maintenance"], queryFn: async () => (await http.get("/system/maintenance")).data });
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (data) setForm(data); }, [data]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  if (!form) return null;
  const save = async () => {
    setSaving(true);
    try { await http.put("/system/maintenance", form); qc.invalidateQueries({ queryKey: ["system-maintenance"] }); qc.invalidateQueries({ queryKey: ["public-maintenance"] }); toast.success("Maintenance settings saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };
  return (
    <div className="max-w-2xl space-y-5" data-testid="system-maintenance">
      <Panel title="Maintenance mode" testId="maintenance-panel">
        <div className="space-y-4">
          <div className={cn("rounded-lg border px-4 py-3", form.enabled ? "border-[#EAB308]/30 bg-[#EAB308]/[0.06]" : "border-white/[0.08] bg-[#0A0A0A]")}>
            <SwitchField label="Enable maintenance mode" hint="Tenant admins, managers, buyers and affiliates see a maintenance page. Super Admin always has access." checked={form.enabled} onChange={(v) => set("enabled", v)} testId="maintenance-enabled" />
          </div>
          <TextField label="Message" value={form.message} onChange={(v) => set("message", v)} testId="maintenance-message" placeholder="We'll be back shortly…" />
          <TextField label="Estimated finish (optional)" value={form.until} onChange={(v) => set("until", v)} testId="maintenance-until" placeholder="2026-07-09 14:00 UTC" hint="Shown on the maintenance page if set." />
          <div className="space-y-2 rounded-lg border border-white/[0.06] bg-[#0A0A0A] p-4">
            <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Access during maintenance</div>
            <SwitchField label="Allow Tenant Admins" checked={form.allow_tenant_admin} onChange={(v) => set("allow_tenant_admin", v)} testId="maintenance-allow-ta" />
            <SwitchField label="Allow Affiliate Portal" checked={form.allow_portal} onChange={(v) => set("allow_portal", v)} testId="maintenance-allow-portal" />
            <SwitchField label="Keep tracking & postbacks active" hint="Recommended — keeps traffic and conversions flowing. Runs at the API level, unaffected by the frontend page." checked={form.keep_tracking} onChange={(v) => set("keep_tracking", v)} testId="maintenance-keep-tracking" />
          </div>
          <Button onClick={save} disabled={saving} data-testid="maintenance-save" className="bg-white text-black hover:bg-white/90">Save settings</Button>
        </div>
      </Panel>
    </div>
  );
}

// ------------------------------------------------------------- Health
function StatusRow({ ok, label, sub }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-white/[0.06] bg-[#0A0A0A] px-4 py-3">
      <span className={cn("h-2.5 w-2.5 shrink-0 rounded-full", ok ? "bg-[#22C55E]" : "bg-[#EF4444]")} />
      <div className="min-w-0"><div className="text-sm text-foreground">{label}</div>{sub && <div className="text-xs text-muted-foreground">{sub}</div>}</div>
    </div>
  );
}

function HealthTab() {
  const { data, refetch, isFetching } = useQuery({ queryKey: ["system-health"], queryFn: async () => (await http.get("/system/health")).data, refetchInterval: 20000 });
  const [pbOpen, setPbOpen] = useState(false);
  if (!data) return null;
  const s = data.services || {}; const r = data.resources || {}; const dbs = data.database || {}; const perf = data.performance || {}; const q = data.queue || {};
  const up = r.uptime_seconds || 0;
  const upStr = up > 3600 ? `${Math.floor(up / 3600)}h ${Math.floor((up % 3600) / 60)}m` : `${Math.floor(up / 60)}m`;
  const gb = (mb) => (mb != null ? (mb / 1024).toFixed(mb / 1024 >= 10 ? 0 : 1) : "—");
  const emailMeta = {
    connected: { label: "Connected", cls: "text-[#22C55E]" },
    error: { label: "Error", cls: "text-[#EF4444]" },
    not_tested: { label: "Saved — not tested", cls: "text-[#EAB308]" },
    not_configured: { label: "Not configured", cls: "text-muted-foreground" },
  }[q.email?.status] || { label: "Not configured", cls: "text-muted-foreground" };

  return (
    <div className="space-y-5" data-testid="system-health">
      <Panel title="System status" testId="health-status"
        right={<Button variant="outline" size="sm" onClick={() => refetch()} className="gap-1.5 border-white/[0.12]" data-testid="health-refresh"><RefreshCw className={cn("h-3.5 w-3.5", isFetching && "animate-spin")} /> Refresh</Button>}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <StatusRow ok={s.backend?.status === "up"} label="Application" sub={s.backend?.status === "up" ? `Online · uptime ${upStr}` : "Offline"} />
          <StatusRow ok={s.mongo?.status === "up"} label="Database" sub={s.mongo?.status === "up" ? `Online · responds in ${s.mongo?.ping_ms ?? "—"} ms` : "Unreachable"} />
          <StatusRow ok={(perf.avg_response_ms ?? 0) < 800} label="API responsiveness" sub={`Avg ${perf.avg_response_ms ?? 0} ms over ${fmtNumber(perf.total_calls)} calls`} />
        </div>
      </Panel>

      <Panel title="Resources" testId="health-resources">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Meter label="CPU" percent={r.cpu_percent} icon={Cpu} sub={r.cpu_percent != null ? `Currently ${r.cpu_percent}% in use` : ""} />
          <Meter label="Memory" percent={r.memory?.percent} icon={MemoryStick} sub={r.memory ? `Using ${gb(r.memory.used_mb)} GB of ${gb(r.memory.total_mb)} GB` : ""} />
          <Meter label="Disk" percent={r.disk?.percent} icon={HardDrive} sub={r.disk ? `Using ${r.disk.used_gb} GB of ${r.disk.total_gb} GB · ${r.disk.free_gb} GB free` : ""} />
        </div>
      </Panel>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Panel title="Database" testId="health-database">
          <div className="space-y-1 text-sm">
            {[["Database size", `${dbs.size_mb ?? "—"} MB`], ["Collections", fmtNumber(dbs.collections)], ["Documents", fmtNumber(dbs.documents)]].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between border-b border-white/[0.05] py-1.5 last:border-0"><span className="text-muted-foreground">{k}</span><span className="text-foreground/90">{v}</span></div>
            ))}
            <div className="mt-2 flex items-start gap-2 rounded-lg border border-white/[0.06] bg-[#0A0A0A] px-3 py-2 text-xs text-muted-foreground">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Backups run automatically on the hosting infrastructure — no action needed here.
            </div>
          </div>
        </Panel>
      </div>

      <Panel title="Delivery queues" testId="health-queue">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <button type="button" onClick={() => setPbOpen(true)} data-testid="queue-postback" className="rounded-lg border border-white/[0.06] bg-[#0A0A0A] px-4 py-3 text-left transition-colors hover:border-white/20">
            <div className="flex items-center justify-between"><span className="text-sm text-foreground">Postbacks</span><Send className="h-3.5 w-3.5 text-muted-foreground" /></div>
            <div className="mt-1.5 flex gap-3 text-xs"><span className="text-muted-foreground">{fmtNumber(q.postback?.sent)} sent</span><span className={cn(q.postback?.failed ? "text-[#EF4444]" : "text-muted-foreground")}>{fmtNumber(q.postback?.failed)} failed</span></div>
            <div className="mt-1 text-[11px] text-[#A78BFA]">View failures →</div>
          </button>
          <div className="rounded-lg border border-white/[0.06] bg-[#0A0A0A] px-4 py-3" data-testid="queue-email">
            <div className="flex items-center justify-between"><span className="text-sm text-foreground">Email</span><span className={cn("text-xs font-medium", emailMeta.cls)} data-testid="queue-email-status">{emailMeta.label}</span></div>
            <div className="mt-1.5 truncate text-xs text-muted-foreground">
              {q.email?.status === "error" ? (q.email?.last_error || "Provider error") :
               q.email?.provider ? `${q.email.provider}${q.email.from_email ? ` · ${q.email.from_email}` : ""}` :
               "Configure Resend in System → Email"}
            </div>
          </div>
        </div>
      </Panel>

      <Panel title="Performance" testId="health-performance" right={<span className="text-xs text-muted-foreground">Avg response <span className="text-foreground">{perf.avg_response_ms} ms</span> · {fmtNumber(perf.total_calls)} calls</span>}>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Top endpoints</div>
            {(perf.top_endpoints || []).map((e, i) => <div key={i} className="flex items-center justify-between border-b border-white/[0.04] py-1 text-xs"><span className="truncate text-foreground/80">{e.endpoint}</span><span className="ml-2 shrink-0 text-muted-foreground">{e.count}×</span></div>)}
          </div>
          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Slowest</div>
            {(perf.slow_endpoints || []).map((e, i) => <div key={i} className="flex items-center justify-between border-b border-white/[0.04] py-1 text-xs"><span className="truncate text-foreground/80">{e.endpoint}</span><span className="ml-2 shrink-0 text-[#EAB308]">{e.avg_ms}ms</span></div>)}
          </div>
          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Top errors</div>
            {(perf.top_errors || []).length ? perf.top_errors.map((e, i) => <div key={i} className="flex items-center justify-between border-b border-white/[0.04] py-1 text-xs"><span className="truncate text-[#EF4444]">{e.error}</span><span className="ml-2 shrink-0 text-muted-foreground">{e.count}</span></div>) : <p className="text-xs text-muted-foreground">No errors 🎉</p>}
          </div>
        </div>
      </Panel>

      <CleanupCenter diskPercent={r.disk?.percent} />
      <FailedPostbacksDialog open={pbOpen} onOpenChange={setPbOpen} />
    </div>
  );
}

function FailedPostbacksDialog({ open, onOpenChange }) {
  const qc = useQueryClient();
  const { data, refetch } = useQuery({ queryKey: ["queue-postback-failed"], queryFn: async () => (await http.get("/system/queue/postback/failed")).data, enabled: open });
  const [busy, setBusy] = useState("");
  const [confirmId, setConfirmId] = useState("");
  const [sel, setSel] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const items = data?.items || [];
  const done = () => { refetch(); qc.invalidateQueries({ queryKey: ["system-health"] }); };
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = items.length > 0 && items.every((it) => sel.has(it.id));
  const toggleAll = () => setSel(allSelected ? new Set() : new Set(items.map((it) => it.id)));
  const retry = async (id) => {
    setBusy(id);
    try { const res = (await http.post(`/system/queue/postback/${id}/retry`)).data; toast[res.ok ? "success" : "error"](res.message); done(); }
    catch (e) { toast.error(e.response?.data?.detail || "Retry failed"); } finally { setBusy(""); }
  };
  const dismiss = async (id) => {
    setBusy(id);
    try { const res = (await http.post(`/system/queue/postback/${id}/dismiss`)).data; toast.success(res.message); setConfirmId(""); done(); }
    catch (e) { toast.error(e.response?.data?.detail || "Dismiss failed"); } finally { setBusy(""); }
  };
  const bulkRetry = async () => {
    setBulkBusy(true); setBulkResult(null);
    try {
      const res = (await http.post("/system/queue/postback/bulk-retry", { ids: [...sel] })).data;
      setBulkResult({ kind: "retry", ...res });
      toast[res.failed ? "warning" : "success"](`Retry: ${res.succeeded} delivered, ${res.failed} failed again`);
      setSel(new Set()); done();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk retry failed"); } finally { setBulkBusy(false); }
  };
  const bulkDismiss = async () => {
    setBulkBusy(true); setBulkResult(null);
    try {
      const res = (await http.post("/system/queue/postback/bulk-dismiss", { ids: [...sel] })).data;
      setBulkResult({ kind: "dismiss", ...res });
      toast.success(`Dismissed ${res.dismissed} postback(s)`);
      setSel(new Set()); done();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk dismiss failed"); } finally { setBulkBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) { setSel(new Set()); setBulkResult(null); } }}>
      <DialogContent className="max-w-3xl border-white/[0.1] bg-[#141417]" data-testid="failed-postbacks-dialog">
        <DialogHeader><DialogTitle>Failed postbacks</DialogTitle></DialogHeader>
        {items.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-white/[0.06] bg-[#0A0A0A] px-3 py-2">
            <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
              <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="pb-select-all" className="h-3.5 w-3.5 accent-white" />
              Select all ({sel.size}/{items.length})
            </label>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={!sel.size || bulkBusy} onClick={bulkDismiss} data-testid="pb-bulk-dismiss" className="gap-1.5 border-white/[0.12] text-muted-foreground hover:text-foreground"><Ban className="h-3.5 w-3.5" /> Dismiss selected</Button>
              <Button size="sm" disabled={!sel.size || bulkBusy} onClick={bulkRetry} data-testid="pb-bulk-retry" className="gap-1.5 bg-white text-black hover:bg-white/90">{bulkBusy ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />} Retry selected</Button>
            </div>
          </div>
        )}
        {bulkResult && (
          <div className="rounded-lg border border-white/[0.08] bg-[#0A0A0A] px-3 py-2 text-xs" data-testid="pb-bulk-result">
            {bulkResult.kind === "retry"
              ? <span><span className="text-[#22C55E]">{bulkResult.succeeded} delivered</span> · <span className={bulkResult.failed ? "text-[#EF4444]" : "text-muted-foreground"}>{bulkResult.failed} failed again</span> (of {bulkResult.total})</span>
              : <span className="text-muted-foreground">Dismissed {bulkResult.dismissed} of {bulkResult.total}</span>}
          </div>
        )}
        {items.length ? (
          <div className="max-h-[55vh] space-y-2 overflow-auto pr-1">
            {items.map((it) => (
              <div key={it.id} className="rounded-lg border border-white/[0.06] bg-[#0A0A0A] p-3" data-testid={`failed-pb-${it.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-2.5">
                    <input type="checkbox" checked={sel.has(it.id)} onChange={() => toggle(it.id)} data-testid={`pb-select-${it.id}`} className="mt-1 h-3.5 w-3.5 shrink-0 accent-white" />
                    <div className="min-w-0">
                      <div className="truncate font-mono text-xs text-foreground/90">{it.method} {it.url}</div>
                      <div className="mt-1 text-xs text-[#EF4444]">{it.http_status ? `HTTP ${it.http_status} · ` : ""}{it.error}</div>
                      <div className="mt-0.5 text-[11px] text-muted-foreground">{fmtDate(it.created_at)}</div>
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button size="sm" variant="outline" disabled={busy === it.id} onClick={() => setConfirmId(it.id)} data-testid={`dismiss-pb-${it.id}`} className="gap-1.5 border-white/[0.12] text-muted-foreground hover:text-foreground">
                      <Ban className="h-3.5 w-3.5" /> Dismiss
                    </Button>
                    <Button size="sm" disabled={busy === it.id} onClick={() => retry(it.id)} data-testid={`retry-pb-${it.id}`} className="gap-1.5 bg-white text-black hover:bg-white/90">
                      {busy === it.id ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />} Retry
                    </Button>
                  </div>
                </div>
                {confirmId === it.id && (
                  <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[#EAB308]/25 bg-[#EAB308]/[0.06] px-3 py-2" data-testid={`dismiss-confirm-${it.id}`}>
                    <span className="text-xs text-[#EAB308]">Dismiss this failed postback? It will no longer be retried automatically.</span>
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => setConfirmId("")} data-testid={`dismiss-cancel-${it.id}`} className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground">Cancel</Button>
                      <Button size="sm" disabled={busy === it.id} onClick={() => dismiss(it.id)} data-testid={`dismiss-yes-${it.id}`} className="h-7 bg-[#EAB308] px-2.5 text-xs text-black hover:bg-[#EAB308]/90">Dismiss</Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground"><CheckCircle2 className="h-4 w-4 text-[#22C55E]" /> No failed postbacks — everything delivered.</div>}
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------------------------------------- Cleanup
const CLEANUP_TASKS = [
  { task: "cleanup-notifications", label: "Old notifications", days: true, desc: "In-app notifications older than the chosen age." },
  { task: "cleanup-audit-logs", label: "Old audit logs", days: true, desc: "Recorded user & admin actions older than the chosen age." },
  { task: "clear-logs", label: "Application logs", desc: "Truncate server log files to free disk space." },
  { task: "clear-temp", label: "Temporary files", desc: "Remove scratch files older than 1 hour." },
  { task: "clear-cache", label: "In-memory cache", desc: "Reset request metrics & stats. Safe — rebuilds automatically." },
];

const AUTO_FIELDS = [
  ["audit_days", "Audit logs", "History of user & admin actions (who did what, and when)."],
  ["notifications_days", "Notifications", "In-app notifications shown to users."],
  ["telegram_logs_days", "Telegram integration logs", "Delivery logs of Telegram notifications across all tenants."],
  ["click_logs_days", "Click logs", "Clicks WITHOUT conversions older than this are removed automatically. Clicks that have at least one conversion are always kept."],
  ["meta_logs_days", "Facebook send logs", "Meta Conversions API send journal only — integrations, clicks and conversions are never touched."],
];

function CleanupCenter({ diskPercent }) {
  const { data: auto } = useQuery({ queryKey: ["system-autocleanup"], queryFn: async () => (await http.get("/system/auto-cleanup")).data });
  const [autoForm, setAutoForm] = useState(null);
  const [confirm, setConfirm] = useState(null); // {task,label,days,desc}
  const [days, setDays] = useState(30);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (auto) setAutoForm(auto); }, [auto]);

  useEffect(() => {
    if (!confirm) { setPreview(null); return; }
    let alive = true; setPreviewing(true);
    const d = confirm.days ? days : 30;
    http.get(`/system/cleanup-preview/${confirm.task}?days=${d}`)
      .then((r) => { if (alive) setPreview(r.data); })
      .catch(() => { if (alive) setPreview({ lines: ["Could not load preview"] }); })
      .finally(() => { if (alive) setPreviewing(false); });
    return () => { alive = false; };
  }, [confirm, days]);

  const run = async () => {
    setBusy(true);
    try { const res = (await http.post(`/system/cleanup/${confirm.task}`, confirm.days ? { days } : {})).data; toast[res.ok ? "success" : "error"](res.message || "Done"); setConfirm(null); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const saveAuto = async () => { try { await http.put("/system/auto-cleanup", autoForm); toast.success("Auto cleanup saved"); } catch { toast.error("Failed"); } };
  const setA = (k, v) => setAutoForm((f) => ({ ...f, [k]: v }));

  return (
    <>
      {diskPercent > 80 && (
        <div className="flex items-center gap-2 rounded-lg border border-[#EF4444]/30 bg-[#EF4444]/[0.08] px-4 py-3 text-sm text-[#EF4444]" data-testid="disk-warning">
          <AlertTriangle className="h-4 w-4 shrink-0" /> Disk is filling up ({diskPercent}%). Clearing logs & temporary files below can help.
        </div>
      )}

      <Panel title="Cleanup" testId="health-cleanup">
        <p className="mb-3 text-xs text-muted-foreground">Free up space and prune stale data. You'll see exactly what will be removed before anything happens.</p>
        <div className="space-y-2">
          {CLEANUP_TASKS.map((c) => (
            <div key={c.task} className="flex items-center justify-between gap-3 rounded-lg border border-white/[0.06] bg-[#0A0A0A] px-3 py-2.5">
              <div className="min-w-0"><div className="text-sm text-foreground">{c.label}</div><div className="text-xs text-muted-foreground">{c.desc}</div></div>
              <Button variant="outline" size="sm" onClick={() => setConfirm(c)} data-testid={`cleanup-${c.task}`} className="shrink-0 gap-1.5 border-white/[0.12]"><Trash2 className="h-3.5 w-3.5" /> Review</Button>
            </div>
          ))}
        </div>
      </Panel>

      {autoForm && (
        <Panel title="Automatic cleanup" testId="health-autocleanup" right={<Button size="sm" onClick={saveAuto} className="bg-white text-black hover:bg-white/90" data-testid="autocleanup-save">Save</Button>}>
          <p className="mb-3 text-xs text-muted-foreground">These run automatically so the platform doesn't grow unbounded over time. Set 0 to keep forever.</p>
          <div className="space-y-3">
            {AUTO_FIELDS.map(([k, label, desc]) => (
              <div key={k} className="flex items-center justify-between gap-4 rounded-lg border border-white/[0.06] bg-[#0A0A0A] px-3 py-2.5">
                <div className="min-w-0"><div className="text-sm text-foreground">{label}</div><div className="text-xs text-muted-foreground">{desc}</div></div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="text-xs text-muted-foreground">keep for</span>
                  <input type="number" value={autoForm[k] ?? 0} onChange={(e) => setA(k, Number(e.target.value))} data-testid={`ac-${k}`}
                    className="w-20 rounded-lg border border-white/[0.12] bg-[#141417] px-2 py-1.5 text-right text-sm text-foreground outline-none focus:border-white/30" />
                  <span className="text-xs text-muted-foreground">days</span>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      <Dialog open={!!confirm} onOpenChange={() => setConfirm(null)}>
        <DialogContent className="border-white/[0.1] bg-[#141417]" data-testid="cleanup-confirm-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-[#EAB308]" /> {confirm?.label}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{confirm?.desc}</p>
          {confirm?.days && (
            <div className="mt-1"><label className="text-xs text-muted-foreground">Remove records older than (days)</label>
              <input type="number" value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="cleanup-days" className="mt-1 w-full rounded-lg border border-white/[0.12] bg-[#0A0A0A] px-3 py-2 text-sm text-foreground outline-none focus:border-white/30" /></div>
          )}
          <div className="mt-2 rounded-lg border border-white/[0.06] bg-[#0A0A0A] p-3 text-sm">
            <div className="mb-1 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Will remove</div>
            {previewing ? <div className="flex items-center gap-2 text-muted-foreground"><RefreshCw className="h-3.5 w-3.5 animate-spin" /> Calculating…</div>
              : (preview?.lines || []).length ? <ul className="list-disc space-y-0.5 pl-5 text-foreground/90" data-testid="cleanup-preview">{preview.lines.map((l, i) => <li key={i}>{l}</li>)}</ul>
              : <div className="text-muted-foreground">Nothing to remove.</div>}
            {preview && preview.reversible === false && <div className="mt-2 text-[11px] text-[#EAB308]">This cannot be undone.</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)} className="border-white/[0.12]">Cancel</Button>
            <Button disabled={busy || previewing} onClick={run} data-testid="cleanup-confirm-btn" className="bg-[#EAB308] text-black hover:bg-[#EAB308]/90">{busy ? "Running…" : "Confirm & remove"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ------------------------------------------------------------- Security
function ScoreRing({ score, level }) {
  const color = score >= 90 ? "#22C55E" : score >= 75 ? "#84CC16" : score >= 50 ? "#EAB308" : "#EF4444";
  return (
    <div className="flex items-center gap-5 rounded-xl border border-white/[0.08] bg-[#141417] p-6" data-testid="security-score">
      <div className="relative flex h-24 w-24 items-center justify-center">
        <svg className="h-24 w-24 -rotate-90" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="8" />
          <circle cx="50" cy="50" r="42" fill="none" stroke={color} strokeWidth="8" strokeLinecap="round" strokeDasharray={`${score / 100 * 264} 264`} />
        </svg>
        <span className="absolute text-2xl font-bold" style={{ color }}>{score}</span>
      </div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Security</div>
        <div className="text-2xl font-semibold" style={{ color }}>{level}</div>
        <div className="text-sm text-muted-foreground">Overall platform security posture (last 24h).</div>
      </div>
    </div>
  );
}

const KIND_LABEL = {
  failed_login: "Login protection — failed logins",
  brute_force: "Login protection — IP auto-blocked (brute force)",
  login_rate_limit: "Login protection — rate limit (429)",
  api_rate_limit: "API protection triggered",
  tracking_abuse: "Tracking protection triggered",
};

// One card per traffic type. Each card shows BOTH layers side by side so the chain is obvious:
// Rate limit (429, soft) → Auto block (Firewall ban, hard). Field keys map 1:1 to the backend
// counters in security_guard.py — do not rename keys here without changing the backend.
const PROTECTION = [
  {
    key: "login", title: "Login protection", icon: LogIn,
    desc: "Sign-in, 2FA and password-recovery requests (/api/auth/login, /api/auth/2fa, /api/auth/recovery) — all organizations.",
    rlKey: "login_attempts_per_minute", rlTestId: "rl-logins", rlUnit: "login attempts / 1 min",
    rlHint: "Counts EVERY login attempt (successful or failed) from one IP. Above the limit, extra attempts get HTTP 429 until the minute ends. Never bans.",
    abKey: "brute_force", abTestId: "ab-brute_force", abUnit: "failed login attempts",
    abHint: "Counts only FAILED login attempts from one IP (any account). Reaching the threshold inside the window adds the IP to the Firewall below.",
    note: "These two layers intentionally count different things: the rate limit counts every login request, the auto block counts only failed logins.",
  },
  {
    key: "api", title: "API protection", icon: Server,
    desc: "Every other /api request — dashboards, CRM, integrations. Login requests are counted here too; /api/system is excluded so a Super Admin can always unblock himself.",
    rlKey: "requests_per_minute", rlTestId: "rl-requests", rlUnit: "API requests / 1 min",
    rlHint: "Counts API requests from one IP. Above the limit, extra requests get HTTP 429 until the minute ends. Never bans.",
    abKey: "api_abuse", abTestId: "ab-api_abuse", abUnit: "API requests",
    abHint: "The SAME API request counter, measured over its own window. Reaching the threshold adds the IP to the Firewall below.",
  },
  {
    key: "tracking", title: "Tracking protection", icon: MousePointerClick,
    desc: "Tracking requests: click redirects (/r/…), Multi Links (/l/…) and postbacks — on every tracking domain across all organizations.",
    rlKey: "clicks_per_minute", rlTestId: "rl-clicks", rlUnit: "tracking requests / 1 min",
    rlHint: "Counts tracking requests from one IP. Above the limit, extra requests get HTTP 429 until the minute ends. Never bans.",
    abKey: "tracking_abuse", abTestId: "ab-tracking_abuse", abUnit: "tracking requests",
    abHint: "The SAME tracking request counter, measured over its own window. Reaching the threshold adds the IP to the Firewall below.",
  },
];

function LimitInput({ value, onChange, testId, disabled, className }) {
  return (
    <input type="number" value={value ?? 0} disabled={disabled} onChange={(e) => onChange(Number(e.target.value))} data-testid={testId}
      className={cn("w-24 rounded-md border border-white/[0.12] bg-[#0A0A0A] px-2.5 py-1.5 text-sm text-foreground outline-none transition-colors focus:border-white/30 disabled:opacity-50", className)} />
  );
}

function SecurityEventDialog({ event, onClose, onBlock }) {
  const { data: ipd } = useQuery({
    queryKey: ["security-ip", event?.ip],
    queryFn: async () => (await http.get(`/system/security/ip/${event.ip}`)).data,
    enabled: !!event?.ip && event.ip !== "unknown",
  });
  if (!event) return null;
  const Row = ({ l, v }) => v ? <div className="flex justify-between gap-4 py-1 text-sm"><span className="text-muted-foreground">{l}</span><span className="text-right text-foreground/90">{v}</span></div> : null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl border-white/[0.1] bg-[#141417]" data-testid="security-event-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: SEV[event.severity] }} />{KIND_LABEL[event.kind] || event.kind}</DialogTitle></DialogHeader>
        <div className="space-y-0.5">
          <Row l="IP" v={<span className="font-mono">{event.ip}</span>} />
          <Row l="Country" v={event.country} />
          <Row l="Endpoint / traffic" v={<span className="font-mono text-xs">{event.endpoint}</span>} />
          <Row l="Network affected" v={event.tenant_name} />
          <Row l="Requests" v={event.count ? `${fmtNumber(event.count)} in ${event.window || "—"}` : null} />
          <Row l="When" v={fmtDate(event.created_at)} />
          <Row l="Auto-blocked" v={event.auto_blocked ? <span className="text-[#EF4444]">Yes</span> : "No"} />
        </div>
        {event.detail && <div className="rounded-lg border border-white/[0.06] bg-[#0A0A0A] p-3 text-xs text-muted-foreground">{event.detail}</div>}
        {ipd && (
          <div className="space-y-2">
            {(ipd.traffic || []).length > 0 && (
              <div className="rounded-lg border border-white/[0.06] bg-[#0A0A0A] p-3">
                <div className="mb-1.5 text-[11px] uppercase tracking-wider text-muted-foreground">Tracking traffic by network (24h)</div>
                {ipd.traffic.map((t, i) => (
                  <div key={i} className="flex justify-between py-0.5 text-xs"><span className="text-foreground/90">{t.tenant}</span><span className="text-muted-foreground">{fmtNumber(t.clicks)} clicks · {fmtNumber(t.duplicates)} dup · {t.offers} offers</span></div>
                ))}
              </div>
            )}
            {(ipd.logins || []).length > 0 && (
              <div className="rounded-lg border border-white/[0.06] bg-[#0A0A0A] p-3">
                <div className="mb-1.5 text-[11px] uppercase tracking-wider text-muted-foreground">Recent login attempts from this IP</div>
                {ipd.logins.slice(0, 6).map((l, i) => (
                  <div key={i} className="flex justify-between py-0.5 text-xs"><span className="text-foreground/90">{l.email || "unknown"}</span><span className={l.success ? "text-[#22C55E]" : "text-[#EF4444]"}>{l.success ? "success" : (l.reason || "failed")} · {fmtDate(l.created_at)}</span></div>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2">
          {event.ip && event.ip !== "unknown" && !event.auto_blocked && !ipd?.block_rule && (
            <Button size="sm" onClick={() => { onBlock(event.ip); onClose(); }} data-testid="event-block-ip" className="gap-1.5 bg-[#EF4444] text-white hover:bg-[#EF4444]/85"><Ban className="h-3.5 w-3.5" /> Block IP</Button>
          )}
          {ipd?.block_rule && <span className="self-center text-xs text-[#EF4444]">IP is blocked{ipd.block_rule.expires_at ? ` until ${fmtDate(ipd.block_rule.expires_at)}` : " (permanent)"}</span>}
          <Button size="sm" variant="outline" onClick={onClose} className="border-white/[0.12]">Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SecurityTab() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["system-security"], queryFn: async () => (await http.get("/system/security")).data, refetchInterval: 20000 });
  const [rl, setRl] = useState(null);
  const [ab, setAb] = useState(null);
  const [fw, setFw] = useState({ type: "block_ip", value: "", reason: "", permanent: true, days: 7 });
  const [selEvent, setSelEvent] = useState(null);
  useEffect(() => { if (data) { setRl((s) => s || data.rate_limiting); setAb((s) => s || data.auto_block); } }, [data]);
  const inv = () => qc.invalidateQueries({ queryKey: ["system-security"] });
  if (!data) return null;

  const block = async (ip, country) => {
    try { await http.post("/system/firewall", { type: "block_ip", value: ip, reason: "Blocked from suspicious IP list" }); toast.success(`Blocked ${ip}`); inv(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const addRule = async () => {
    if (!fw.value.trim()) return toast.error("Enter an IP or country code");
    try { await http.post("/system/firewall", fw); toast.success("Firewall rule added"); setFw((f) => ({ ...f, value: "", reason: "" })); inv(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const delRule = async (id) => { try { await http.delete(`/system/firewall/${id}`); inv(); } catch (e) { toast.error("Failed"); } };
  const saveProtection = async () => {
    try {
      await http.put("/system/security/rate-limit", rl);
      const res = (await http.put("/system/security/auto-block", ab)).data;
      setAb(res);
      toast.success("Protection settings saved");
      inv();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const setAbField = (cat, field, v) => setAb((s) => ({ ...s, [cat]: { ...s[cat], [field]: v } }));

  return (
    <div className="space-y-5" data-testid="system-security">
      <ScoreRing score={data.score} level={data.level} />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[["Clicks (24h)", fmtNumber(data.clicks_total), "#fff"], ["Duplicate rate", `${data.duplicate_rate}%`, data.duplicate_rate > 40 ? "#EF4444" : data.duplicate_rate > 20 ? "#EAB308" : "#22C55E"], ["Failed logins", fmtNumber(data.failed_logins), data.failed_logins ? "#EAB308" : "#6B7280"], ["Suspicious IPs", fmtNumber((data.suspicious_ips || []).length), (data.suspicious_ips || []).length ? "#EF4444" : "#6B7280"]].map(([l, v, c]) => (
          <div key={l} className="rounded-lg border border-white/[0.06] bg-[#0A0A0A] p-4"><div className="text-xl font-semibold" style={{ color: c }}>{v}</div><div className="mt-1 text-xs text-muted-foreground">{l}</div></div>
        ))}
      </div>

      {(data.attacks || []).length > 0 && (
        <Panel title="Attack detection" testId="security-attacks">
          <div className="space-y-2">
            {data.attacks.map((a, i) => (
              <div key={i} className="flex items-center gap-3 rounded-lg border px-4 py-3" style={{ borderColor: `${SEV[a.severity]}44`, background: `${SEV[a.severity]}0f` }} data-testid={`attack-${i}`}>
                <AlertTriangle className="h-5 w-5 shrink-0" style={{ color: SEV[a.severity] }} />
                <div><div className="text-sm font-semibold" style={{ color: SEV[a.severity] }}>{a.type}</div><div className="text-xs text-muted-foreground">{a.detail}</div></div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      <Panel title="Live security events" testId="security-events">
        {(data.events || []).length ? (
          <div className="max-h-[340px] space-y-2 overflow-auto pr-1">{data.events.map((e, i) => (
            <button key={e.id || i} type="button" onClick={() => setSelEvent(e)} data-testid={`security-event-${i}`}
              className="flex w-full items-start gap-2.5 rounded-lg border border-white/[0.06] bg-[#0A0A0A] px-3 py-2 text-left transition-colors hover:border-white/20">
              <span className="mt-1 h-2 w-2 shrink-0 rounded-full" style={{ background: SEV[e.severity] }} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 text-sm text-foreground/90">
                  {KIND_LABEL[e.kind] || e.kind}
                  {e.ip && e.ip !== "unknown" && <span className="font-mono text-xs text-muted-foreground">{e.ip}</span>}
                  {e.auto_blocked && <span className="rounded bg-[#EF4444]/15 px-1.5 py-0.5 text-[10px] text-[#EF4444]">auto-blocked</span>}
                  {e.tenant_name && <span className="rounded bg-white/[0.06] px-1.5 py-0.5 text-[10px] text-muted-foreground">{e.tenant_name}</span>}
                </div>
                <div className="truncate text-xs text-muted-foreground">{e.detail}</div>
              </div>
              <span className="shrink-0 text-[11px] text-muted-foreground">{fmtDate(e.created_at)}</span>
            </button>))}</div>
        ) : <div className="flex items-center gap-2 text-sm text-muted-foreground"><CheckCircle2 className="h-4 w-4 text-[#22C55E]" /> No notable security events.</div>}
      </Panel>

      <Panel title="Top suspicious IPs" testId="security-suspicious">
        {(data.suspicious_ips || []).length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-white/[0.08] text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="py-2">IP</th><th>Country</th><th className="text-right">Clicks</th><th className="text-right">Requests</th><th>Reason</th><th className="text-right">Action</th></tr></thead>
              <tbody>{data.suspicious_ips.map((r, i) => (
                <tr key={i} className="border-b border-white/[0.04]" data-testid={`sus-ip-${i}`}>
                  <td className="py-2 font-mono text-foreground/90">{r.ip}</td><td className="text-muted-foreground">{r.country}</td>
                  <td className="text-right">{fmtNumber(r.clicks)}</td><td className="text-right">{fmtNumber(r.requests)}</td>
                  <td className="text-xs text-[#EAB308]">{r.reason}</td>
                  <td className="text-right">{r.blocked ? <span className="text-xs text-[#EF4444]">Blocked</span> : <button onClick={() => block(r.ip, r.country)} data-testid={`block-ip-${i}`} className="inline-flex items-center gap-1 rounded border border-[#EF4444]/30 px-2 py-0.5 text-xs text-[#EF4444] hover:bg-[#EF4444]/10"><Ban className="h-3 w-3" /> Block</button>}</td>
                </tr>))}</tbody>
            </table>
          </div>
        ) : <p className="text-sm text-muted-foreground">No suspicious IP activity detected.</p>}
      </Panel>

      {ab && rl && (
        <Panel title="Protection layers" testId="security-protection"
          right={<Button size="sm" onClick={saveProtection} className="bg-white text-black hover:bg-white/90" data-testid="protection-save">Save</Button>}>
          <div className="mb-4 space-y-1.5 rounded-lg border border-white/[0.06] bg-[#0A0A0A] p-3 text-xs leading-relaxed text-muted-foreground">
            <p>Each traffic type below has two independent layers, counted per IP:</p>
            <p><span className="font-semibold text-[#EAB308]">Rate limit (soft)</span> — a per-minute limit. Above it, extra requests get <span className="text-foreground">HTTP 429</span> until the minute ends. It only slows the client down and never bans.</p>
            <p><span className="font-semibold text-[#EF4444]">Auto block (hard)</span> — its own threshold over its own window. When an IP reaches it, the IP is added to the <span className="text-foreground">Firewall</span> below as a Block IP rule with an [AUTO] reason and stays blocked until you remove the rule manually. Super Admins with the “Security threat” Telegram notification get an instant alert.</p>
            <p>The layers are fully independent: requests already rejected with 429 still count toward Auto block, and Auto block keeps working even when rate limiting is switched off. All settings are global — every organization, PP and tracking domain.</p>
          </div>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <SwitchField label="Enable rate limiting (all three rate limits below)" hint="Auto block is NOT affected by this switch — each category has its own Auto block toggle." checked={rl.enabled} onChange={(v) => setRl((s) => ({ ...s, enabled: v }))} testId="rl-enabled" />
            <div className="text-xs text-muted-foreground">Requests rejected with 429 so far: <span className="text-foreground">{fmtNumber(rl.blocked_count)}</span></div>
          </div>
          <div className="space-y-3">
            {PROTECTION.map((p) => (
              <div key={p.key} className="rounded-xl border border-white/[0.08] bg-[#0A0A0A] p-4" data-testid={`protection-${p.key}`}>
                <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-foreground"><p.icon className="h-4 w-4 text-muted-foreground" /> {p.title}</div>
                <p className="mb-3 text-xs text-muted-foreground">{p.desc}</p>
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                  <div className={cn("rounded-lg border border-[#EAB308]/20 bg-[#EAB308]/[0.04] p-3", !rl.enabled && "opacity-60")}>
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <span className="rounded bg-[#EAB308]/15 px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-[#EAB308]">STEP 1 · RATE LIMIT → 429</span>
                      {!rl.enabled && <span className="text-[10px] text-muted-foreground" data-testid={`rl-off-note-${p.key}`}>off — only Auto block applies</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <LimitInput value={rl[p.rlKey]} onChange={(v) => setRl((s) => ({ ...s, [p.rlKey]: v }))} testId={p.rlTestId} disabled={!rl.enabled} />
                      <span className="text-xs text-foreground/80">{p.rlUnit}</span>
                    </div>
                    <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">{p.rlHint}</p>
                  </div>
                  <div className={cn("rounded-lg border border-[#EF4444]/20 bg-[#EF4444]/[0.04] p-3", !ab[p.abKey]?.enabled && "opacity-60")}>
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                      <span className="rounded bg-[#EF4444]/15 px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-[#EF4444]">STEP 2 · AUTO BLOCK → FIREWALL BAN</span>
                      <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                        {ab[p.abKey]?.enabled ? "On" : "Off"}
                        <Switch data-testid={`${p.abTestId}-enabled`} checked={!!ab[p.abKey]?.enabled} onCheckedChange={(v) => setAbField(p.abKey, "enabled", v)} />
                      </label>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-foreground/80">
                      <LimitInput value={ab[p.abKey]?.threshold} onChange={(v) => setAbField(p.abKey, "threshold", v)} testId={`${p.abTestId}-threshold`} disabled={!ab[p.abKey]?.enabled} />
                      <span>{p.abUnit} within</span>
                      <LimitInput value={ab[p.abKey]?.window_min} onChange={(v) => setAbField(p.abKey, "window_min", v)} testId={`${p.abTestId}-window`} disabled={!ab[p.abKey]?.enabled} className="w-16" />
                      <span>min → IP goes to Firewall</span>
                    </div>
                    <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">{p.abHint}</p>
                  </div>
                </div>
                {p.note && <p className="mt-2 flex items-start gap-1.5 text-[11px] text-[#38BDF8]"><Info className="mt-0.5 h-3 w-3 shrink-0" /> {p.note}</p>}
              </div>
            ))}
          </div>
        </Panel>
      )}

      <Panel title="Firewall" testId="security-firewall">
        <div className="mb-4 grid grid-cols-1 gap-2 rounded-lg border border-white/[0.06] bg-[#0A0A0A] p-3 sm:grid-cols-[160px_1fr_1fr_auto]">
          <SelectField value={fw.type} onChange={(v) => setFw((f) => ({ ...f, type: v }))} testId="fw-type"
            options={[{ value: "block_ip", label: "Block IP" }, { value: "allow_ip", label: "Allow IP" }, { value: "block_country", label: "Block Country" }, { value: "allow_country", label: "Allow Country" }]} />
          <TextField value={fw.value} onChange={(v) => setFw((f) => ({ ...f, value: v }))} testId="fw-value" placeholder={fw.type.endsWith("country") ? "Country code e.g. RU" : "IP e.g. 203.0.113.5"} />
          <TextField value={fw.reason} onChange={(v) => setFw((f) => ({ ...f, reason: v }))} testId="fw-reason" placeholder="Reason (optional)" />
          <Button onClick={addRule} data-testid="fw-add" className="gap-1.5 bg-white text-black hover:bg-white/90"><Zap className="h-4 w-4" /> Add</Button>
        </div>
        <label className="mb-3 flex items-center gap-2 text-xs text-muted-foreground">
          <input type="checkbox" checked={!fw.permanent} data-testid="fw-temp" onChange={(e) => setFw((f) => ({ ...f, permanent: !e.target.checked }))} className="h-3.5 w-3.5 accent-white" />
          Temporary block {!fw.permanent && <>for <input type="number" value={fw.days} onChange={(e) => setFw((f) => ({ ...f, days: Number(e.target.value) }))} className="w-14 rounded border border-white/[0.12] bg-[#0A0A0A] px-2 py-0.5 text-xs" data-testid="fw-days" /> days</>}
        </label>
        {(data.firewall || []).length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-white/[0.08] text-left text-xs uppercase tracking-wider text-muted-foreground"><th className="py-2">Type</th><th>Value</th><th>Reason</th><th>Duration</th><th>By</th><th>Date</th><th></th></tr></thead>
              <tbody>{data.firewall.map((r) => (
                <tr key={r.id} className="border-b border-white/[0.04]" data-testid={`fw-rule-${r.id}`}>
                  <td className="py-2"><span className={cn("rounded px-1.5 py-0.5 text-xs", r.type.startsWith("block") ? "bg-[#EF4444]/15 text-[#EF4444]" : "bg-[#22C55E]/15 text-[#22C55E]")}>{r.type.replace("_", " ")}</span></td>
                  <td className="font-mono text-foreground/90">{r.value}</td><td className="text-xs text-muted-foreground">{r.reason || "—"}</td>
                  <td className="text-xs text-muted-foreground">{r.permanent ? "Permanent" : `Until ${r.expires_at ? fmtDate(r.expires_at) : "—"}`}</td>
                  <td className="text-xs text-muted-foreground">{r.created_by || "—"}</td><td className="text-xs text-muted-foreground">{fmtDate(r.created_at)}</td>
                  <td className="text-right"><button onClick={() => delRule(r.id)} data-testid={`fw-del-${r.id}`} className="text-muted-foreground hover:text-[#EF4444]"><Trash2 className="h-3.5 w-3.5" /></button></td>
                </tr>))}</tbody>
            </table>
          </div>
        ) : <p className="text-sm text-muted-foreground">No firewall rules yet.</p>}
      </Panel>
      {selEvent && <SecurityEventDialog event={selEvent} onClose={() => setSelEvent(null)} onBlock={(ip) => block(ip)} />}
    </div>
  );
}

export default function System() {
  const [tab, setTab] = useState("maintenance");
  const render = { maintenance: <MaintenanceTab />, health: <HealthTab />, security: <SecurityTab />, email: <EmailSettingsTab />, backups: <BackupsTab /> }[tab];
  return (
    <div className="space-y-6" data-testid="system-page">
      <PageHeader title="System" subtitle="Maintenance, health monitoring, security & infrastructure control." />
      <div className="no-scrollbar flex flex-wrap gap-1 overflow-x-auto border-b border-white/[0.08]">
        {SUBTABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`system-tab-${t.id}`}
            className={cn("relative flex items-center gap-1.5 whitespace-nowrap px-3 py-2 text-sm transition-colors", tab === t.id ? "text-foreground" : "text-muted-foreground hover:text-foreground")}>
            <t.icon className="h-3.5 w-3.5" /> {t.label}
            {tab === t.id && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-white" />}
          </button>
        ))}
      </div>
      <div data-testid={`system-panel-${tab}`}>{render}</div>
    </div>
  );
}
